import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { setAuthToken, setUnauthorizedHandler } from "@solarkey/api-client";

import { login as apiLogin, type Credentials } from "./authClient";

type AuthStatus = "anonymous" | "authenticating" | "authenticated";

interface AuthState {
  status: AuthStatus;
  token: string | null;
  error: string | null;
  login: (credentials: Credentials) => Promise<void>;
  logout: () => void;
}

const STORAGE_KEY = "sk.auth.token";
const AuthContext = createContext<AuthState | null>(null);

let _freshLogin = false;
export function consumeFreshLogin(): boolean {
  const v = _freshLogin;
  _freshLogin = false;
  return v;
}

function readToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // storage blocked (private mode / edge) — treat as anonymous
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Set the restored token on the api-client synchronously, before children render
  // and fire their first query, so a reload never races an un-authed request.
  const [token, setToken] = useState<string | null>(() => {
    const t = readToken();
    setAuthToken(t);
    return t;
  });
  const [status, setStatus] = useState<AuthStatus>(token ? "authenticated" : "anonymous");
  const [error, setError] = useState<string | null>(null);
  // Signing out has to take the ANSWERS with it, not just the token. The client is
  // one instance for the app's lifetime, so every figure, site list and alarm the
  // previous person loaded stayed in it: the next person to sign in on the same
  // browser — a shared control-room machine is the normal case — could be shown
  // another tenant's data until each query happened to refetch. Clearing the cache
  // is what makes signing out mean something locally.
  const queryClient = useQueryClient();

  // Attach a restored token (page reload / return visit) to the api-client so the
  // first request after mount already carries the bearer, not just after a login.
  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  // When any authed request returns 401 (the 1-hour token expired), clear the
  // session and return to login with a message — never strand on "API error 401".
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuthToken(null);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* storage unavailable */
      }
      // An expired session leaves the same residue as a sign-out, so it is cleared
      // the same way. `clear()` also cancels anything still in flight, which would
      // otherwise land after the token is gone and 401 again.
      queryClient.clear();
      setToken(null);
      setStatus("anonymous");
      setError("Your session expired. Please sign in again.");
      window.history.replaceState(null, "", "/");
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  const login = useCallback(async (credentials: Credentials) => {
    setStatus("authenticating");
    setError(null);
    try {
      const result = await apiLogin(credentials);
      // Attach the bearer to the api-client SYNCHRONOUSLY, before setToken mounts
      // the authed tree. React runs a child's effect before its parent's, so if we
      // left this to the [token] effect the SessionGate bootstrap would fire its
      // first request (GET /v1/session/capabilities) tokenless and 401.
      setAuthToken(result.token);
      try {
        localStorage.setItem(STORAGE_KEY, result.token);
      } catch {
        /* storage unavailable — keep the token in memory only */
      }
      _freshLogin = true;
      setToken(result.token);
      setStatus("authenticated");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
      setStatus("anonymous");
    }
  }, []);

  const logout = useCallback(() => {
    setAuthToken(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
    // Drop every answer the signed-out person loaded, before the next one arrives.
    queryClient.clear();
    setToken(null);
    setStatus("anonymous");
    // Reset the URL so the next login lands on Home, not the last visited screen.
    window.history.replaceState(null, "", "/");
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ status, token, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
